/**
 * @file DatePicker 单日选择
 * @description 自定义月历浮层；值用 `YYYY-MM-DD`；Portal + 视口边界 + fade。
 *
 * @whenToUse 表单选单个日期（生日、生效日等）。
 * @whenNotToUse 日期区间（用 DateRangePicker）；精确到时分（需另做 DateTime）。
 */

import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CalendarDays } from 'lucide-react'
import { cn } from '@/lib/cn.ts'
import { formatDate, parseDate } from '@/lib/date.ts'
import {
  computeDropdownBox,
  floatingPanelStyle,
  type FloatingBox,
} from '@/lib/floating.ts'
import { Calendar } from '@/components/ui/calendar.tsx'

export type DatePickerProps = {
  /** 当前值 `YYYY-MM-DD` */
  value?: string
  /** 值变化 */
  onChange?: (value: string) => void
  /** 占位 */
  placeholder?: string
  /** 禁用 */
  disabled?: boolean
  /** 校验失败 */
  invalid?: boolean
  /** 触发器 class */
  className?: string
  /** id（配合 Label） */
  id?: string
}

export function DatePicker({
  value,
  onChange,
  placeholder = '选择日期',
  disabled = false,
  invalid = false,
  className,
  id,
}: DatePickerProps) {
  const panelId = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const [box, setBox] = useState<FloatingBox | null>(null)

  const selected = value ? parseDate(value) : null
  const [viewMonth, setViewMonth] = useState(
    () => selected ?? new Date(),
  )

  useEffect(() => {
    if (selected) {
      setViewMonth(new Date(selected.getFullYear(), selected.getMonth(), 1))
    }
  }, [value])

  const updateBox = () => {
    const el = triggerRef.current
    if (!el) return
    setBox(computeDropdownBox(el.getBoundingClientRect(), 4, 288))
  }

  useEffect(() => {
    if (open) {
      setMounted(true)
      updateBox()
      const frame = requestAnimationFrame(() => setVisible(true))
      return () => cancelAnimationFrame(frame)
    }
    setVisible(false)
    const timer = window.setTimeout(() => setMounted(false), 200)
    return () => window.clearTimeout(timer)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onWin = () => updateBox()
    window.addEventListener('resize', onWin)
    window.addEventListener('scroll', onWin, true)
    return () => {
      window.removeEventListener('resize', onWin)
      window.removeEventListener('scroll', onWin, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onPointer = (event: MouseEvent) => {
      const t = event.target as Node
      if (triggerRef.current?.contains(t)) return
      if (panelRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <>
      <button
        id={id}
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={panelId}
        className={cn(
          'inline-flex h-9 w-full items-center justify-between gap-2 rounded-lg border-2 bg-raised px-3 text-left text-sm transition-opacity duration-200 disabled:cursor-not-allowed disabled:opacity-50',
          invalid ? 'border-danger' : 'border-border',
          className,
        )}
        onClick={() => setOpen((v) => !v)}
      >
        <span
          className={cn(
            'min-w-0 flex-1 truncate',
            value ? 'text-foreground' : 'text-muted',
          )}
        >
          {value || placeholder}
        </span>
        <CalendarDays className="size-4 shrink-0 text-muted" aria-hidden />
      </button>

      {mounted && box && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={panelRef}
              id={panelId}
              role="dialog"
              className={cn(
                'fixed z-50 overflow-hidden rounded-lg border border-border bg-raised transition-opacity duration-200',
                visible ? 'opacity-100' : 'opacity-0',
              )}
              style={floatingPanelStyle(box)}
            >
              <Calendar
                viewMonth={viewMonth}
                onViewMonthChange={setViewMonth}
                selected={selected}
                onSelectDay={(day) => {
                  onChange?.(formatDate(day))
                  setOpen(false)
                }}
              />
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
